import { intl } from '@fullstory/intl';
import {
  Aggregation,
  BuiltInNumericProperty,
  NumericAggregation,
  NumericAggregationMethod,
  NumericProperty,
  UniqueAggregation,
  UniquePropertyAggregation,
} from '@fullstory/proto/fs/api/metrics';
import { SpecObjectProperty } from '@fullstory/proto/fs/services/search-settings';
import { BySlug, SpecObjectInfo } from '@fullstory/redux';
import { FieldScope, parseUnscopedFieldName } from '@fullstory/search-vars';
import { assertExhaustive } from '@fullstory/typeutils';
import {
  NumericAggregationBuiltInPropertyOption,
  NumericAggregationCustomPropertyOption,
  NumericAggregationPropertyOption,
  UniqueAggregationPropertyOption,
} from './metricTypes';

type DefaultAggregationOption = {
  type: Aggregation.aggregation_method_keys.default;
  method?: never;
};
type UniqueAggregationOption = {
  type: Aggregation.aggregation_method_keys.unique;
  method: UniqueAggregation;
};
type UniquePropertyAggregationOption = {
  type: Aggregation.aggregation_method_keys.uniqueProperty;
  method?: never;
};
type NumericAggregationOption = {
  type: Aggregation.aggregation_method_keys.numeric;
  method: NumericAggregationMethod;
};

export type MetricAggregationOption =
  | DefaultAggregationOption
  | UniqueAggregationOption
  | UniquePropertyAggregationOption
  | NumericAggregationOption;

/**
 * Converts an `Aggregation` to the corresponding `MetricAggregationOption`.
 * @param aggregation - the current aggregation method
 */
export const aggregationToOption = (
  aggregation: Aggregation | null,
): MetricAggregationOption | null => {
  if (!aggregation) return null;
  if (aggregation.default)
    return { type: Aggregation.aggregation_method_keys.default } as MetricAggregationOption;
  if (aggregation.unique)
    return { type: Aggregation.aggregation_method_keys.unique, method: aggregation.unique };
  if (aggregation.uniqueProperty != null)
    return { type: Aggregation.aggregation_method_keys.uniqueProperty };
  if (aggregation.numeric) {
    return {
      type: Aggregation.aggregation_method_keys.numeric,
      method: aggregation.numeric.aggregation,
    };
  }
  return null;
};

const MetricAggregationName = {
  INVALID: '',
  EVENT_COUNT: intl.formatMessage({ defaultMessage: 'Events', id: 'ZvKSfJ' }),
  UNIQUE_INDIVIDUAL: intl.formatMessage({ defaultMessage: 'Unique users', id: 'JSU3ce' }),
  UNIQUE_SESSION: intl.formatMessage({ defaultMessage: 'Unique sessions', id: '+EySJ4' }),
  UNIQUE_PAGEVIEW: intl.formatMessage({ defaultMessage: 'Unique page views', id: 'YwlsQO' }),
  UNIQUE_PROPERTY: intl.formatMessage({ defaultMessage: 'Unique property', id: '2gn6IV' }),
  NUMERIC_AVERAGE: intl.formatMessage({
    defaultMessage: 'Average',
    id: 'tyf3RU',
    description: 'An average numeric aggregation name or label',
  }),
  NUMERIC_SUM: intl.formatMessage({ defaultMessage: 'Sum', id: 'YYkniy' }),
  NUMERIC_MIN: intl.formatMessage({ defaultMessage: 'Min', id: 'A4AHkc' }),
  NUMERIC_MAX: intl.formatMessage({ defaultMessage: 'Max', id: 'kkjl2v' }),
  NUMERIC_MEDIAN: intl.formatMessage({ defaultMessage: 'Median', id: 'WrSL3+' }),
  PERCENTILE: intl.formatMessage({ defaultMessage: 'Percentile', id: 'bw0ffx' }),
};

const UniqueAggregationNames = {
  [UniqueAggregation.UNIQUE_AGGREGATION_INVALID]: MetricAggregationName.INVALID,
  [UniqueAggregation.UNIQUE_AGGREGATION_INDIVIDUAL]: MetricAggregationName.UNIQUE_INDIVIDUAL,
  [UniqueAggregation.UNIQUE_AGGREGATION_SESSION]: MetricAggregationName.UNIQUE_SESSION,
  [UniqueAggregation.UNIQUE_AGGREGATION_PAGEVIEW]: MetricAggregationName.UNIQUE_PAGEVIEW,
};

const NumericAggregationNames = {
  [NumericAggregationMethod.NUMERIC_AGGREGATION_METHOD_INVALID]: MetricAggregationName.INVALID,
  [NumericAggregationMethod.NUMERIC_AGGREGATION_METHOD_AVERAGE]:
    MetricAggregationName.NUMERIC_AVERAGE,
  [NumericAggregationMethod.NUMERIC_AGGREGATION_METHOD_SUM]: MetricAggregationName.NUMERIC_SUM,
  [NumericAggregationMethod.NUMERIC_AGGREGATION_METHOD_MIN]: MetricAggregationName.NUMERIC_MIN,
  [NumericAggregationMethod.NUMERIC_AGGREGATION_METHOD_MAX]: MetricAggregationName.NUMERIC_MAX,
  [NumericAggregationMethod.NUMERIC_AGGREGATION_METHOD_MEDIAN]:
    MetricAggregationName.NUMERIC_MEDIAN,
  [NumericAggregationMethod.NUMERIC_AGGREGATION_METHOD_PERCENTILE]:
    MetricAggregationName.PERCENTILE,
};

export const metricAggregationOptionToName = (option: MetricAggregationOption): string => {
  switch (option.type) {
    case Aggregation.aggregation_method_keys.default:
      return MetricAggregationName.EVENT_COUNT;
    case Aggregation.aggregation_method_keys.unique:
      return UniqueAggregationNames[option.method];
    case Aggregation.aggregation_method_keys.uniqueProperty:
      return MetricAggregationName.UNIQUE_PROPERTY;
    case Aggregation.aggregation_method_keys.numeric:
      return NumericAggregationNames[option.method];
    default:
      return assertExhaustive(option);
  }
};

export const fieldNameToNumericAggregationPropertyOption = (
  fieldName: string,
  type: Exclude<
    NumericProperty.field_keys,
    NumericProperty.field_keys.builtInProperty | NumericProperty.field_keys.specPropertySlug
  >,
): NumericAggregationCustomPropertyOption => {
  const field = parseUnscopedFieldName(fieldName, FieldScope.NoScope);
  return {
    field,
    value: fieldName,
    type,
  };
};

/**
 * Translates a built-in numeric property to its string name.
 * @param property - any built-in numeric property
 */
export const builtInNumericPropertyToName = (property: BuiltInNumericProperty) => {
  switch (property) {
    case BuiltInNumericProperty.NUMERIC_PROPERTY_CONSOLE_ERRORS_ON_PAGE:
      return intl.formatMessage({ defaultMessage: 'Console errors on page', id: 'iYisoJ' });
    case BuiltInNumericProperty.NUMERIC_PROPERTY_CONSOLE_WARNINGS_ON_PAGE:
      return intl.formatMessage({ defaultMessage: 'Console warnings on page', id: 'P96NkA' });
    case BuiltInNumericProperty.NUMERIC_PROPERTY_DISPLAY_HEIGHT:
      return intl.formatMessage({ defaultMessage: 'Display height', id: '5eE/2Y' });
    case BuiltInNumericProperty.NUMERIC_PROPERTY_DISPLAY_WIDTH:
      return intl.formatMessage({ defaultMessage: 'Display width', id: 'pfLwhe' });
    case BuiltInNumericProperty.NUMERIC_PROPERTY_DOM_CONTENT_DURATION:
      return intl.formatMessage({ defaultMessage: 'DOMContentLoaded time', id: 'MMPC3C' });
    case BuiltInNumericProperty.NUMERIC_PROPERTY_FIRST_MEANINGFUL_PAINT_DURATION:
      return intl.formatMessage({ defaultMessage: 'First Contentful Paint time', id: 'jChSYo' });
    case BuiltInNumericProperty.NUMERIC_PROPERTY_LOAD_EVENT_DURATION:
      return intl.formatMessage({ defaultMessage: 'Page load time', id: 'Xuqiz4' });
    case BuiltInNumericProperty.NUMERIC_PROPERTY_PAGE_ACTIVE_DURATION:
      return intl.formatMessage({ defaultMessage: 'Page active duration', id: 'FSxnis' });
    case BuiltInNumericProperty.NUMERIC_PROPERTY_PAGE_DURATION:
      return intl.formatMessage({ defaultMessage: 'Page duration', id: 'kCBS9D' });
    case BuiltInNumericProperty.NUMERIC_PROPERTY_SCROLL_DEPTH:
      return intl.formatMessage({ defaultMessage: 'Scroll depth', id: 'uAdfN6' });
    case BuiltInNumericProperty.NUMERIC_PROPERTY_TIME_SINCE_PAGE_LOAD:
      return intl.formatMessage({ defaultMessage: 'Time since page load', id: 'CCNkgW' });
    case BuiltInNumericProperty.NUMERIC_PROPERTY_TIME_SINCE_SESSION_START:
      return intl.formatMessage({ defaultMessage: 'Time since session start', id: 'UHAMpE' });
    case BuiltInNumericProperty.NUMERIC_PROPERTY_TIME_DURATION_RENDERED:
      return intl.formatMessage({ defaultMessage: 'Time duration rendered', id: 'C2E/kE' });
    case BuiltInNumericProperty.NUMERIC_PROPERTY_TIME_DURATION_VISIBLE:
      return intl.formatMessage({ defaultMessage: 'Time duration visible', id: 'Ac8jrf' });
    case BuiltInNumericProperty.NUMERIC_PROPERTY_VIEWPORT_HEIGHT:
      return intl.formatMessage({ defaultMessage: 'Viewport height', id: 'MqPUO/' });
    case BuiltInNumericProperty.NUMERIC_PROPERTY_VIEWPORT_WIDTH:
      return intl.formatMessage({ defaultMessage: 'Viewport width', id: '9+b1sa' });
    case BuiltInNumericProperty.NUMERIC_PROPERTY_PAGEVIEW_TOTAL_DURATION:
      return intl.formatMessage({ defaultMessage: 'Time on page', id: '7NbcIG' });
    case BuiltInNumericProperty.NUMERIC_PROPERTY_PAGEVIEW_ACTIVE_DURATION:
      return intl.formatMessage({ defaultMessage: 'Time on page active', id: 'Yc6Lz1' });
    case BuiltInNumericProperty.NUMERIC_PROPERTY_PAGEVIEW_INACTIVE_DURATION:
      return intl.formatMessage({ defaultMessage: 'Time on page inactive', id: 'mDIttA' });
    case BuiltInNumericProperty.NUMERIC_PROPERTY_CUMULATIVE_LAYOUT_SHIFT:
      return intl.formatMessage({ defaultMessage: 'Cumulative Layout Shift', id: 'h6rdDs' });
    case BuiltInNumericProperty.NUMERIC_PROPERTY_FIRST_INPUT_DELAY:
      return intl.formatMessage({ defaultMessage: 'First Input Delay time', id: 'neK/1c' });
    case BuiltInNumericProperty.NUMERIC_PROPERTY_LARGEST_CONTENTFUL_PAINT_DURATION:
      return intl.formatMessage({ defaultMessage: 'Largest Contentful Paint time', id: 'B1aLit' });
    case BuiltInNumericProperty.NUMERIC_PROPERTY_INTERACTION_TO_NEXT_PAINT:
      return intl.formatMessage({ defaultMessage: 'Interaction to Next Paint time', id: 'vnN8K3' });
    case BuiltInNumericProperty.NUMERIC_PROPERTY_TIME_TO_INTERACTIVE:
      return intl.formatMessage({ defaultMessage: 'Time to Interactive', id: 'dE1fN6' });
    case BuiltInNumericProperty.NUMERIC_PROPERTY_TOTAL_BLOCKING_TIME:
      return intl.formatMessage({ defaultMessage: 'Total Blocking time', id: 'k4UD3d' });
    case BuiltInNumericProperty.NUMERIC_PROPERTY_TIME_TO_FIRST_BYTE:
      return intl.formatMessage({ defaultMessage: 'Time to First Byte', id: 'LlmkZl' });
    case BuiltInNumericProperty.NUMERIC_PROPERTY_INVALID:
      return '';
    default:
      assertExhaustive(property);
  }
};

export const builtInNumericPropertyToNumericAggregationPropertyOption = (
  property: BuiltInNumericProperty,
): NumericAggregationBuiltInPropertyOption => ({
  value: property,
  type: NumericProperty.field_keys.builtInProperty,
});

const specPropertyOptionDisplay = (
  slug: string,
  specObjectsBySlug: BySlug<SpecObjectInfo>,
  specPropertiesBySlug: BySlug<SpecObjectProperty>,
) => {
  const property = specPropertiesBySlug[slug];
  if (!property) {
    return null;
  }
  const lastIdx = slug.lastIndexOf('/');
  const parentSlug = lastIdx > 0 ? slug.substring(0, lastIdx) : '';
  const parentDisplayName = specObjectsBySlug[parentSlug]?.displayName || parentSlug;
  return {
    slug: property.slug,
    displayName: property.metadata?.displayName || property.metadata?.name || slug,
    parentDisplayName,
  };
};

export const aggregationToNumericAggregationPropertyOption = (
  aggregation: NumericAggregation,
  specObjectsBySlug: BySlug<SpecObjectInfo>,
  specPropertiesBySlug: BySlug<SpecObjectProperty>,
): NumericAggregationPropertyOption | null => {
  const property = aggregation?.property;
  if (!property) {
    return null;
  }
  const oneof = NumericProperty.field_oneof(property);
  if (!oneof) {
    return null;
  }
  switch (oneof.opt) {
    case NumericProperty.field.builtInProperty:
      return builtInNumericPropertyToNumericAggregationPropertyOption(oneof.val);
    case NumericProperty.field.specPropertySlug: {
      const display = specPropertyOptionDisplay(oneof.val, specObjectsBySlug, specPropertiesBySlug);
      if (!display) {
        return null;
      }
      return {
        type: NumericProperty.field_keys.specPropertySlug,
        displayName: display.displayName,
        parentDisplayName: display.parentDisplayName,
        value: display.slug,
      };
    }
    default:
      // remainder are all custom property types that can be handled the same
      return fieldNameToNumericAggregationPropertyOption(oneof.val, oneof.key);
  }
};

export const fieldNameToUniqueAggregationPropertyOption = (
  fieldName: string,
  type: Exclude<
    UniquePropertyAggregation.property_keys,
    UniquePropertyAggregation.property_keys.specPropertySlug
  >,
): UniqueAggregationPropertyOption => {
  const field = parseUnscopedFieldName(fieldName, FieldScope.NoScope);
  return {
    field,
    value: fieldName,
    type,
  };
};

export const aggregationToUniqueAggregationPropertyOption = (
  aggregation: UniquePropertyAggregation,
  specObjectsBySlug: BySlug<SpecObjectInfo>,
  specPropertiesBySlug: BySlug<SpecObjectProperty>,
): UniqueAggregationPropertyOption | null => {
  if (!aggregation) {
    return null;
  }
  const oneof = UniquePropertyAggregation.property_oneof(aggregation);
  if (!oneof) {
    return null;
  }
  if (oneof.opt === UniquePropertyAggregation.property.specPropertySlug) {
    const display = specPropertyOptionDisplay(oneof.val, specObjectsBySlug, specPropertiesBySlug);
    if (!display) {
      return null;
    }
    return {
      type: UniquePropertyAggregation.property_keys.specPropertySlug,
      value: display.slug,
      displayName: display.displayName,
      parentDisplayName: display.parentDisplayName,
    };
  }
  // all unique property aggregation options are custom properties that can be handled the same
  return fieldNameToUniqueAggregationPropertyOption(oneof.val, oneof.key);
};
